
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SpeedInsights } from "@vercel/speed-insights/react"
import { Analytics } from "@vercel/analytics/react"
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import React, { Suspense } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/hooks/useLanguage";
import { CartProvider } from "@/contexts/CartContext";
import { CategoryProvider } from "@/contexts/CategoryContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import PrivateRoute from "@/components/auth/PrivateRoute";

// Lazy load page components
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const PasswordReset = React.lazy(() => import("./pages/PasswordReset"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Products = React.lazy(() => import("./pages/Products"));
const ProductDetail = React.lazy(() => import("./pages/ProductDetail"));
const Checkout = React.lazy(() => import("./pages/Checkout"));
const OrderSuccess = React.lazy(() => import("./pages/OrderSuccess"));
const OrderHistory = React.lazy(() => import("./pages/OrderHistory"));
const OrderDetails = React.lazy(() => import("./pages/OrderDetails"));
const AccountInfo = React.lazy(() => import("./pages/AccountInfo"));
const BulkOrderImport = React.lazy(() => import("./pages/BulkOrderImport"));
const B2BPriceDemo = React.lazy(() => import("./pages/B2BPriceDemo"));
const B2BCustomerDemo = React.lazy(() => import("./pages/B2BCustomerDemo"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

// Admin components
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrders'));
const AdminManagement = React.lazy(() => import('./pages/admin/AdminManagement'));
const AdminCache = React.lazy(() => import('./pages/admin/AdminCache'));
const AdminAuditLogs = React.lazy(() => import('./pages/admin/AdminAuditLogs'));
const AdminAuditStats = React.lazy(() => import('./pages/admin/AdminAuditStats'));
const AdminActivityLogs = React.lazy(() => import('./pages/admin/AdminActivityLogs'));
const AdminConfig = React.lazy(() => import('./pages/admin/AdminConfig'));
const AdminApiKeys = React.lazy(() => import('./pages/admin/AdminApiKeys'));
const AdminAccessLogs = React.lazy(() => import('./pages/admin/AdminAccessLogs'));
const AdminErpApiLogs = React.lazy(() => import('./pages/admin/AdminErpApiLogs'));
const AdminPaymentStatus = React.lazy(() => import('./pages/admin/AdminPaymentStatus'));
const AdminRoute = React.lazy(() => import('./components/admin/AdminRoute'));

const AppRoutes = () => {
  const location = useLocation();
  const suspenseFallback = <div>Loading...</div>;

  if (location.pathname.startsWith('/admin')) {
    return (
      <Suspense fallback={suspenseFallback}>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/payment-status" element={<AdminPaymentStatus />} />
              <Route path="/admin/management" element={<AdminManagement />} />
              <Route path="/admin/cache" element={<AdminCache />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
              <Route path="/admin/audit-stats" element={<AdminAuditStats />} />
              <Route path="/admin/activity-logs" element={<AdminActivityLogs />} />
              <Route path="/admin/config" element={<AdminConfig />} />
              <Route path="/admin/api-keys" element={<AdminApiKeys />} />
              <Route path="/admin/b2b-access-logs" element={<AdminAccessLogs />} />
              <Route path="/admin/erp-api-logs" element={<AdminErpApiLogs />} />
            </Route>
          </Route>
          <Route path="/admin/*" element={<NotFound />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={suspenseFallback}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Protected routes */}
        <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />
        <Route path="/product/:productId" element={<PrivateRoute><ProductDetail /></PrivateRoute>} />
        <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
        <Route path="/order-success" element={<PrivateRoute><OrderSuccess /></PrivateRoute>} />
        <Route path="/order-history" element={<PrivateRoute><OrderHistory /></PrivateRoute>} />
        <Route path="/order/:orderId" element={<PrivateRoute><OrderDetails /></PrivateRoute>} />
        <Route path="/account-info" element={<PrivateRoute><AccountInfo /></PrivateRoute>} />
        <Route path="/bulk-import" element={<PrivateRoute><BulkOrderImport /></PrivateRoute>} />
        <Route path="/b2b-price-demo" element={<PrivateRoute><B2BPriceDemo /></PrivateRoute>} />
        <Route path="/b2b-customer-demo" element={<PrivateRoute><B2BCustomerDemo /></PrivateRoute>} />
        
        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <div className="min-h-screen">
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 text-center">
          <h2 className="text-2xl font-bold mb-4 text-destructive">An unexpected error occurred</h2>
          <p className="mb-6 text-muted-foreground max-w-md">
            We apologize for the inconvenience. The error has been logged and we will look into it.
          </p>
          <button 
            onClick={resetError}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
          {process.env.NODE_ENV === 'development' && (
            <pre className="mt-8 p-4 bg-muted rounded text-left overflow-auto max-w-full text-xs">
              {error.message}
            </pre>
          )}
        </div>
      )}
      onError={(error, componentStack) => {
        console.error('Caught by ErrorBoundary:', error, componentStack);
      }}
    >
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <CategoryProvider>
                <CartProvider>
                  <AdminAuthProvider>
                    <AppRoutes />
                  </AdminAuthProvider>
                </CartProvider>
              </CategoryProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </TooltipProvider>
      <SpeedInsights />
      <Analytics />
    </Sentry.ErrorBoundary>
  </div>
);

export default App;
